import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { AdminService } from '../../services/AdminService';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

export default function Settings() {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState({
    siteName: '',
    maintenanceMode: false,
    allowNewRegistrations: true,
    maxProductImages: 6,
    minOrderAmount: 0,
    maxOrderItems: 10,
    shippingFee: 0,
    taxRate: 0,
    supportEmail: '',
    supportPhone: '',
    termsUrl: '',
    privacyUrl: '',
    aboutUrl: ''
  });

  // Fetch current settings
  const { isLoading, error } = useQuery(
    'appSettings',
    () => AdminService.getAppSettings(),
    {
      onSuccess: (data) => {
        if (data) {
          setSettings(data);
        }
      }
    }
  );

  // Update settings mutation
  const updateMutation = useMutation(
    (newSettings) => AdminService.updateAppSettings(newSettings),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('appSettings');
        enqueueSnackbar('Settings updated successfully', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    }
  );

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setSettings(prev => ({
      ...prev,
      [name]: event.target.type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    updateMutation.mutate(settings);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error.message}
      </Alert>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        App Settings
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* General Settings */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  General Settings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Site Name"
                    name="siteName"
                    value={settings.siteName}
                    onChange={handleChange}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.maintenanceMode}
                        onChange={handleChange}
                        name="maintenanceMode"
                      />
                    }
                    label="Maintenance Mode"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.allowNewRegistrations}
                        onChange={handleChange}
                        name="allowNewRegistrations"
                      />
                    }
                    label="Allow New Registrations"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Product Settings */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Product Settings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Max Product Images"
                    name="maxProductImages"
                    value={settings.maxProductImages}
                    onChange={handleChange}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Order Settings */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Order Settings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Minimum Order Amount"
                    name="minOrderAmount"
                    value={settings.minOrderAmount}
                    onChange={handleChange}
                  />
                  <TextField
                    type="number"
                    fullWidth
                    label="Maximum Order Items"
                    name="maxOrderItems"
                    value={settings.maxOrderItems}
                    onChange={handleChange}
                  />
                  <TextField
                    type="number"
                    fullWidth
                    label="Shipping Fee"
                    name="shippingFee"
                    value={settings.shippingFee}
                    onChange={handleChange}
                  />
                  <TextField
                    type="number"
                    fullWidth
                    label="Tax Rate (%)"
                    name="taxRate"
                    value={settings.taxRate}
                    onChange={handleChange}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Settings */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Contact Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Support Email"
                    name="supportEmail"
                    value={settings.supportEmail}
                    onChange={handleChange}
                  />
                  <TextField
                    fullWidth
                    label="Support Phone"
                    name="supportPhone"
                    value={settings.supportPhone}
                    onChange={handleChange}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Legal Settings */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Legal Pages
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Terms of Service URL"
                    name="termsUrl"
                    value={settings.termsUrl}
                    onChange={handleChange}
                  />
                  <TextField
                    fullWidth
                    label="Privacy Policy URL"
                    name="privacyUrl"
                    value={settings.privacyUrl}
                    onChange={handleChange}
                  />
                  <TextField
                    fullWidth
                    label="About Us URL"
                    name="aboutUrl"
                    value={settings.aboutUrl}
                    onChange={handleChange}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={updateMutation.isLoading}
          >
            {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </form>
    </Container>
  );
} 